<template>
  <div
    class="add-product-modal">
    <app-dialog
      v-model="showProductModal"
      :value.sync="showProductModal"
      :width="useComparisonTool ? 1600 : 1200"
      content-class="v-dialog__form v-dialog__product-expanded-view"
      persistent
      @keydown.esc="showProductModal = false">
      <div class="dialog-content">
        <v-card v-if="showProductModal">
          <v-card-title>
            <div class="d-flex align-center">
              <span v-if="isEditModalVariant">Update Product</span>
              <span v-else-if="isAddModalVariant">Create Product</span>
              <span v-else-if="isReadOnlyModalVariant">Product Details</span>
              <span v-else-if="isSaveAsNewVariant">Save as new product</span>
              <span v-else-if="isUpdateReferencedProduct">Update Referenced Product</span>
            </div>
            <v-icon
              color="#fff"
              @click="showProductModal = false">
              mdi-close
            </v-icon>
          </v-card-title>
          <v-card-text
            class="items-wrapper">
            <div
              class="items h-100vh">
              <BulkProduct
                v-if="isAddModalVariant && selectedCollectionToCreateProduct"
                :collection="selectedCollectionToCreateProduct" />
              <div
                v-if="(isReadOnlyModalVariant || isEditModalVariant) && getRoutesForProductModal"
                class="mb-3 cursor-pointer"
                @click="copyToClipBoard(renderProductLink)">
                Copy Product Link
              </div>
              <AppFormRow
                v-if="isReadOnlyModalVariant && !hasAccessToProduct && productCollectionName"
                header-text="Collection">
                <template #rowCell>
                  <a @click="goNextPageBlank">
                    {{ productCollectionName }}
                  </a>
                </template>
              </AppFormRow>
              <div class="product-details-flex-wrapper">
                <app-search-rows-provider
                  ref="searchWrapper"
                  placeholder="Search by field name"
                  :cols="useComparisonTool ? '12' : '11'"
                  :search-wrapper-class="isReadOnlyModalVariant && !hasAccessToProduct && productCollectionName
                    ? 'search-items__wrapper-collections-search'
                    : 'search-items__wrapper-collections'"
                  :items="renderHeaders"
                  :search-items-height="searchItemsHeight"
                  @hook:mounted="mountedappSearchRowsProvider">
                  <template #items="{ searchItems, searchOn }">
                    <component
                      :is="getProductComponent"
                      :auto-hide-unpopulated-fields="autoHideUnpopulatedFields"
                      :is-dialog-collection="isDialogCollection"
                      :collection-id="collectionIdForViewSchema"
                      :headers="searchItems"
                      :item="itemToRendering"
                      :field-errors="fieldErrors"
                      :search-mode="searchOn"
                      :mapped-dropdowns-of-view="getMappedDropdownsOfView"
                      view="productCreate"
                      @updateProductField="updateProductField" />
                  </template>
                </app-search-rows-provider>
                <div
                  v-if="moveTipsAndTricks"
                  class="product-details-aside-wrapper">
                  <component
                    :is="getProductComponent"
                    :auto-hide-unpopulated-fields="autoHideUnpopulatedFields"
                    :is-dialog-collection="isDialogCollection"
                    :collection-id="collectionIdForViewSchema"
                    :headers="renderAsideHeaders"
                    :item="itemToRendering"
                    :field-errors="fieldErrors"
                    :search-mode="false"
                    :mapped-dropdowns-of-view="getMappedDropdownsOfView"
                    :is-aside-form="true"
                    view="productCreate"
                    @updateProductField="updateProductField" />
                </div>
              </div>
            </div>
          </v-card-text>
          <DialogActions
            ref="dialogActions"
            :can-work-with-modify-product="canWorkWithModifyProduct"
            :user-role-in-collection="userRoleInCollection"
            :item="item"
            :followed-collection-id="followedCollectionId"
            :has-access-to-product="hasAccessToProduct"
            :is-store-product-preload="isStoreProductPreload"
            :selected-collection-to-create-product="selectedCollectionToCreateProduct"
            :product-id="productId"
            :editable="editable"
            @save="onSave"
            @changeToEdit="changeToEdit"
            @is-followed-product="isFollowedProduct = $event"
            @show-product-modal="showProductModal = $event"
            @delete-current-product="deleteCurrentProduct" />
        </v-card>
      </div>
    </app-dialog>
  </div>
</template>
<script>
import AppFormRow from '@/components/App/AppFormRow';
import ProductCellFormList from '@/components/Product/ProductCell/ProductCellCollectionsFormList';
import ProductCellFormListToComparison
  from '@/components/Product/ProductCell/ProductCellCollectionsFormList/ProductCellCollectionsFormListToComparison';
import updateCollectionProductField from '@/mixins/updateCollectionProductField';
import DisableEditMode from '@/mixins/DisableEditMode';
import ProductAddToProject from '@/components/Product/ProductAddToProject';
import ComparisonApi from '@/services/graphql/comparison';
import {
  askAndPerform,
  isLimitOfProducts,
  copyToClipBoard, accessHelper, hasViewerAccess,
} from '@/utils';
import {
  TYPE_FOLLOW,
  TYPE_DUPLICATE,
  TYPE_EDIT,
  TYPE_READONLY, PRIVATE,
  DEBOUNCE_TIME_TO_RESIZE_EVENTS,
} from '@/constants';
import {
  mapState, mapMutations, mapActions, mapGetters,
} from 'vuex';
import BulkProduct from '@/components/Collections/CollectionCreateProductBulk';
import AppSearchRowsProvider from '@/components/App/AppSearchRowsProvider';
import DialogActions from '@/components/Collections/CollectionsCreateProductDialog/DialogActions/index.vue';
import { parseGetRows } from '@/utils/manageGetRows';
import {
  isAsideHeader,
  sortAsideHeadersLast,
} from '@/utils/productHeaders';
import {
  setCollectionSchema,
  setCollectionSchemaMapping,
  setSelectedCollectionView,
  setMappedDropdownsOfView,
} from '@/utils/collectionSchemaAndViews';
import { debounce } from 'lodash';
import { FOLLOW_EDIT_REASON } from '@/constants/userPermissions';
import { COLLECTION } from '@/constants/cores';
import API from '@/services/graphql';
import CollectionsApi from '@/services/graphql/collections';
export default {
  name: 'CollectionsCreateProductDialog',
  components: {
    BulkProduct,
    ProductCellFormList,
    ProductCellFormListToComparison,
    AppFormRow,
    AppSearchRowsProvider,
    ProductAddToProject,
    DialogActions,
  },
  mixins: [updateCollectionProductField, DisableEditMode],
  props: {
    isSourceLibraryId: {
      type: Boolean,
      default: false,
    },
    isDialogCollection: {
      type: Boolean,
      default: true,
    },
    statusDialog: {
      type: Boolean,
      default: false,
    },
    isStatusDialogFromState: {
      type: Boolean,
      default: true,
    },
    sharedLinkCollectionId: {
      type: String,
      default: () => '',
    },
    editable: {
      type: Boolean,
      default: () => false,
    },
    isStoreProductPreload: {
      type: Boolean,
      default: false,
    },
    collectionIdForGetSchema: {
      type: String,
      default: '',
    },
    propLibraryId: {
      type: String,
      default: '',
    },
    localProduct: {
      type: Object,
      default: () => {
      },
    },
    isAllowComparison: {
      type: Boolean,
      default: true,
    },
    autoHideUnpopulatedFields: {
      type: Boolean,
      default: false,
    },
    /**
     * Determines whether to use the same schema and views.
     *
     * @type {boolean}
     * @default false
     * @description If set to true, the component will use the same schema and views as the rendered page.
     */
    useTheSameSchemaAndViews: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      rowData: {
      },
      showTooltip: true,
      fieldErrors: [],
      productId: null,
      selectedCollectionToCreateProduct: null,
      TYPE_FOLLOW,
      TYPE_DUPLICATE,
      productsToComparison: [],
      isFollowedProduct: false,
      userRoleInCollection: null,
      collectionsSchemaV2: {
      },
      selectedCollectionView: {
      },
      searchItemsHeight: '',
    };
  },
  computed: {
    ...mapGetters('Collections', [
      'updatedProduct',
      'isEditModalVariant',
      'isAddModalVariant',
      'isReadOnlyModalVariant',
      'isUpdateReferencedProduct',
      'isSaveAsNewVariant',
      'collectionGroupsForFollowProduct',
      'collectionsWithPermissions',
      'getSaveAsNewCollections',
      'findSaveAsNewCollectionsById',
      'isDisallowClosingProductDialogAfterUpdate',
      'getRoutesForProductModal',
    ]),
    ...mapGetters('Libraries', ['getDefaultLibraryId']),
    ...mapState(['showSpinner', 'activeHeader', 'isMobile']),
    ...mapState({
      generalAccessToken: state => state.accessToken,
    }),
    ...mapState('Workspace', ['activeWorkspaceId']),
    ...mapState('Collections', ['privateCollectionsList', 'productToUpdate', 'publishedCollectionsList',
      'showCreateProductModal', 'productCollectionName', 'productCollectionLink', 'currentCollectionSharingOption', 'isCollectionInCustomLibrary']),
    ...mapGetters('UserRoles', ['canAddProductToCollection', 'canFollowProduct', 'findUserRoleInLibraryForActiveHeader', 'usersRolesListInCollection']),
    ...mapGetters('FeatureFlags', [
      'useActionsProductFromSearch',
      'useEditedFollowComparisonTool',
      'useLightfairRequiredFields',
      'creatorUnableToCreateFollowProducts',
      'moveTipsAndTricks',
    ]),
    getMappedDropdownsOfView() {
      const { selectedCollectionView: view } = this;
      const { dropdowns } = view || {
      };
      return setMappedDropdownsOfView(dropdowns);
    },
    accessToken() {
      return this.$route?.query?.accessToken;
    },
    getProductComponent() {
      const { useComparisonTool } = this;
      if (!useComparisonTool) return 'ProductCellFormList';
      return 'ProductCellFormListToComparison';
    },
    isEditMode() {
      return this.currentProduct?.mode === 'edit';
    },
    useComparisonTool() {
      const {
        useEditedFollowComparisonTool: flag,
        isEditMode,
        isAllowComparison,
        isAddModalVariant,
        usersRolesListInCollection,
        findUserRoleInLibraryForActiveHeader,
      } = this;
      return flag && !isAddModalVariant && isEditMode && isAllowComparison && hasViewerAccess(findUserRoleInLibraryForActiveHeader ?? usersRolesListInCollection);
    },
    currentProduct() {
      const {
        isStatusDialogFromState = false,
        productToUpdate = {
        },
        localProduct = {
        },
      } = this;
      return !isStatusDialogFromState ? localProduct : productToUpdate;
    },
    isEditableFromSearch() {
      return this.currentProduct?.editable || false;
    },
    hasAccessToProduct() {
      const { useActionsProductFromSearch: flag, isEditableFromSearch, editable } = this;
      return (isEditableFromSearch && flag) || editable;
    },
    collectionIdForViewSchema() {
      return this.collectionIdForGetSchema || this.collectionIdForCreateProduct || this.getDefaultLibraryId;
    },
    isEditModalRelatedVariant() {
      const { isEditModalVariant, isUpdateReferencedProduct } = this;
      return isEditModalVariant || isUpdateReferencedProduct;
    },
    renderProductLink() {
      const workspace = `/workspace/${this.activeWorkspaceId}`;
      const isPrivateCollection = this?.currentCollectionSharingOption?.type == PRIVATE;
      const renderWsString = isPrivateCollection ? workspace : '';
      let additionalPath = '';
      const urlParams = new URLSearchParams();
      if (['shared-collection-product-link', 'embed-collection-product-link'].includes(this.$route.name)) {
        additionalPath = '/shared';
        Object.entries(this.$route.query).forEach(entry => {
          urlParams.set(entry[0], entry[1] ?? '');
        });
      }
      const query = urlParams?.toString();
      // return `https://${window.location.hostname}${renderWsString}/collection/${this.$route.params.id}${additionalPath}/product/${this.item.id}${query ? '?'.concat(query) : ''}`;
      return `https://${window.location.hostname}${renderWsString}/collection/${this.collectionIdForCreateProduct}${additionalPath}/product/${this.item.id}${query ? '?'.concat(query) : ''}`;
    },
    isDisabledCreateButton() {
      return (this.isAddModalVariant || this.isSaveAsNewVariant) && !this.selectedCollectionToCreateProduct?.id;
    },
    canWorkWithModifyProduct() {
      const {
        useActionsProductFromSearch: flag,
        isEditableFromSearch,
        findUserRoleInLibraryForActiveHeader,
        usersRolesListInCollection,
        isUpdateReferencedProduct,
      } = this;
      const { members } = this.activeHeader || {
      };
      let role = findUserRoleInLibraryForActiveHeader;
      if (isEditableFromSearch && flag) return accessHelper(true);
      else if (isUpdateReferencedProduct) {
        role = this.roleInResource;
      } else if (members) {
        role = usersRolesListInCollection;
      }
      return this.canModifyProduct(role, this.isFollowedProduct && FOLLOW_EDIT_REASON);
    },
    renderHeaders() {
      return [
        {
          name: 'Choose Collection',
          value: 'Choose Collection',
          selectValue: this.selectedCollectionToCreateProduct?.id,
          items: this.isSaveAsNewVariant ? this.getSaveAsNewCollections : this.collectionsList,
          column: {
            type: 'SELECT',
          },
        },
        ...this.schema,
      ];
    },
    renderAsideHeaders() {
      return this.schema.filter(isAsideHeader);
    },
    item: {
      get() {
        return this.rowData;
      },
      set(value) {
        this.$set(this.rowData, value.col, value.value);
      },
    },
    itemToRendering() {
      const { useComparisonTool: flag, item } = this;
      return !flag ? item : this.productsToComparison;
    },
    showProductModal: {
      get() {
        return this.isStatusDialogFromState ? this.updatedProduct : this.statusDialog;
      },
      set(value) {
        this.isStatusDialogFromState ? this.changeUpdateProductMode(value) : this.$emit('update:statusDialog', value);
      },
    },
    collectionsSchemaAccordingToFlag() {
      const { creatorUnableToCreateFollowProducts: flag, useTheSameSchemaAndViews } = this;
      return flag && !useTheSameSchemaAndViews ? this.collectionsSchemaV2 : this.collectionsSchema;
    },
    schema() {
      const { schema } = this.collectionsSchemaAccordingToFlag || {
      };
      if (!schema) return [];
      return schema.toSorted(sortAsideHeadersLast);
    },
    requiredFieldsAccordingToResolution() {
      const { requiredFields } = this.collectionsSchemaAccordingToFlag || {
      };
      if (!this.useLightfairRequiredFields || this.isMobile) {
        return requiredFields;
      }
      // https://app.asana.com/0/1204380836259938/1204467940299595/f
      return ['B', 'C', 'D', 'G', 'AO'];
    },
    formatedRequiredFields() {
      return this.requiredFieldsAccordingToResolution.map(el => {
        const item = this.schema.find((shemaObj) => shemaObj.id === el);
        return item.value;
      });
    },
    collectionsList() {
      return this.collectionsWithPermissions();
    },
    removeText() {
      return 'This will DELETE the Product(s) from this Collection. Are you sure?';
    },
  },
  watch: {
    currentProduct(val) {
      if (val) {
        if (this.isUpdateReferencedProduct) {
          this.getSchemaAndViewsToCollection({
            collectionId: val?.referencedCollectionId,
          });
        } else {
          this.getProductId(val);
        }
      }
    },
    selectedCollectionToCreateProduct(value) {
      if (!value) return;
      const { id: collectionId } = value;
      const { creatorUnableToCreateFollowProducts: flag } = this;
      if (this.isSaveAsNewVariant || flag) {
        this.getSchemaAndViewsToCollection({
          collectionId,
        });
      }
    },
    async showProductModal(value) {
      if (!value) {
        this.onCloseModal();
        this.productsToComparison = [];
        if (this.getRoutesForProductModal.includes(this.$route.name)) {
          const matched = this.$route.matched;
          const { propLibraryId } = this;
          const libraryId = propLibraryId || this.$route.params?.libraryId;
          this.$router.replace({
            name: matched[matched.length - 2]?.name,
            params: {
              disableProxyDialog: true,
              id: this.$route.params.id,
              ...this.isCollectionInCustomLibrary && libraryId && {
                libraryId,
              },
            },
            query: {
              ...this.$route.query,
              ...!this.canWorkWithModifyProduct?.allowed
              && this.currentCollectionSharingOption?.type == 'unlistedPublish'
              && {
                accessToken: ['shared-collection-product-link', 'embed-collection-product-link'].includes(this.$route.name)
                  ? this.$route.query?.accessToken
                  : this.currentCollectionSharingOption?.accessToken,
              },
            },
          }).catch(() => {
          });
        }
        return;
      }
      this.initRowData();
      if (this.isUpdateReferencedProduct) return;
      if (this.isSaveAsNewVariant) {
        this.showProductModalInSchedule();
        return;
      }
      this.showProductModalInCollections();
      this.checkComparisonExecution();
      await this.getUserRoleInCollection();
    },
  },
  destroyed() {
    window.removeEventListener('resize', this.getSearchResultHeight);
  },
  methods: {
    mountedappSearchRowsProvider() {
      window.addEventListener('resize', this.getSearchResultHeight);
    },
    ...mapActions({
      getCustomLibraryCollectionsList: 'Collections/getCustomLibraryCollectionsList',
      getCollectionsSchemaV1: 'Collections/getCollectionsSchema',
      getCollectionsViewsV1: 'Collections/getCollectionsViews',
      getCollectionsList: 'Collections/getCollectionsList',
      getlistCollectionsForProduct: 'Collections/getlistCollectionsForProduct',
      deleteProduct: 'Collections/deleteProduct',
      deleteProductFromSearch: 'Collections/deleteProductFromSearch',
    }),
    /**
      * Calculates the height of the search results based on the current viewport size and element offsets.
      */
    getSearchResultHeight: debounce(function () {
      const { $refs: refs } = this;
      if (!refs) {
        this.resetSearchResultHeight();
        return;
      }
      const { dialogActions, searchWrapper } = refs || {
      };
      const { $el: el } = dialogActions || {
      };
      if (!el) {
        this.resetSearchResultHeight();
        return;
      }
      const { clientHeight } = el || {
      };
      const { searchResults } = searchWrapper?.$refs || {
      };
      if (!searchResults) {
        this.resetSearchResultHeight();
        return;
      }
      const { offsetTop } = searchResults || {
      };
      const { innerHeight } = window || {
      };
      // Determine the maximum height for the search results
      // based on the current viewport height
      const MAX_HEIGHT_COEFFICIENT = 0.9; // 90% of the viewport height for mdAndUp breakpoint
      // Calculate the available height for the search results
      const calcInnerHeight = this.$vuetify.breakpoint.smAndDown ? innerHeight : MAX_HEIGHT_COEFFICIENT * innerHeight;
      // Calculate the height of the search items based on the available space
      const height = calcInnerHeight - (offsetTop + clientHeight);
      // Set the height of the search items CSS property
      this.searchItemsHeight = height <= 0 ? '' : `height: ${height}px`;
    }, DEBOUNCE_TIME_TO_RESIZE_EVENTS),
    resetSearchResultHeight() {
      this.searchItemsHeight = '';
    },
    async  getUserRoleInCollection() {
      const { collectionId: resourceId } = this.item || {
      };
      if (!resourceId) return;
      try {
        const { data } = await API.getMyRoleInResource({
          workspaceId: this.activeWorkspaceId,
          resourceType: COLLECTION,
          resourceId,
        });
        this.userRoleInCollection = data.response;
      } catch (err) {
        console.error(err);
      }
    },
    setProductsToComparison({ origin, follow, loading = false, notAccessible = false } = {
    }) {
      this.productsToComparison = [
        {
          shortTitle: 'Reference',
          title: 'Original Follow Reference (Read Only)',
          product: origin,
          readOnly: true,
          loading,
          notAccessible,
        },
        {
          shortTitle: 'Follow',
          title: 'Edited/Customized Follow (Editable)',
          readOnly: false,
          product: follow || this.item,
          loading: false,
        },
      ];
    },
    async getProductToComparison() {
      try {
        const {
          activeWorkspaceId: workspaceId,
          getMasterCollection: tableId,
          getMasterProduct: rowId,
        } = this;
        if (!tableId || !rowId) {
          this.setProductsToComparison({
            origin: {
            },
            notAccessible: true,
          });
          return;
        }
        this.setProductsToComparison({
          loading: true,
          origin: {
          },
        });
        const { data } = await ComparisonApi.getRowDiffProduct({
          tableType: 'collection',
          tableId,
          rowId,
          rowVersionId: '',
          compareVersion: '',
          workspaceId,
        });
        const { fields = [] } = data?.response || {
        };
        const parsedFields = parseGetRows(fields);
        this.setProductsToComparison({
          origin: parsedFields,
        });
      } catch (e) {
        this.setProductsToComparison({
          origin: {
          },
          notAccessible: true,
        });
        console.log('getProductToComparison errr', e);
      } finally {
        this.spinner(false);
      }
    },
    async checkComparisonExecution() {
      const { useComparisonTool } = this;
      if (!useComparisonTool) return;
      const { isEditMode } = this;
      if (!isEditMode) return;
      this.getProductToComparison();
    },
    askAndPerform,
    copyToClipBoard,
    ...mapMutations(['spinner']),
    ...mapMutations('Collections', [
      'changeUpdateProductMode',
      'setProductToUpdate',
      'changeProductModalVariant',
      'changeProductCollectionLink',
      'changeProductCollectionName',
    ]),
    goNextPageBlank() {
      const routeData = this.$router.resolve(this.productCollectionLink);
      window.open(routeData.href, '_blank');
    },
    async initRowData() {
      if (this.currentProduct) {
        this.rowData = this.currentProduct;
      } else {
        this.schema.forEach((item) => {
          this.$set(this.rowData, item.text, this.getInitialValue(item.column.type));
        });
      }
    },
    async showProductModalInSchedule() {
      this.spinner(true);
      await this.getlistCollectionsForProduct({
        projectId: this.rowData.projectId,
      });
      const item = this.findSaveAsNewCollectionsById(this.getDefaultLibraryId);
      const [firstCollection = null] = this.getSaveAsNewCollections || [];
      if (!isLimitOfProducts(item)) {
        this.selectedCollectionToCreateProduct = item || firstCollection;
      }
      this.spinner(false);
    },
    async getSchemaAndViewsToCollection(data) {
      if (!data) return;
      this.spinner(true);
      await this.getCollectionsViews(data);
      await this.getCollectionsSchema(data);
      this.getSearchResultHeight();
      this.spinner(false);
    },
    async getCollectionsViews(values) {
      const { creatorUnableToCreateFollowProducts: flag, propLibraryId: libraryId } = this;
      const data = {
        ...values,
        ...libraryId && {
          libraryId,
        },
      };
      flag ? await this.getCollectionsViewsV2(data) : await this.getCollectionsViewsV1(data);
    },
    async getCollectionsSchema(values) {
      const { creatorUnableToCreateFollowProducts: flag, propLibraryId: libraryId } = this;
      const data = {
        ...values,
        ...libraryId && {
          libraryId,
        },
      };
      flag ? await this.getCollectionsSchemaV2(data) : await this.getCollectionsSchemaV1(data);
    },
    async getCollectionsViewsV2(values = {
    }) {
      try {
        const { generalAccessToken: accessToken, activeWorkspaceId: workspaceId } = this;
        const { data } = await CollectionsApi.collectionsViews({
          ...values,
          workspaceId,
          ...(accessToken && {
            accessToken,
          }),
        });
        const { response: views } = data;
        this.selectedCollectionView = setSelectedCollectionView(views);
      } catch (err) {
        console.log('getCollectionsViews err', err);
      }
    },
    async getCollectionsSchemaV2(values = {
    }) {
      try {
        const { generalAccessToken: accessToken, activeWorkspaceId: workspaceId } = this;
        const { data } = await CollectionsApi.collectionsSchema({
          ...values,
          workspaceId,
          ...(accessToken && {
            accessToken,
          }),
        });
        const { response: collectionsSchema } = data || {
        };
        const { schema } = collectionsSchema || {
        };
        this.collectionsSchemaV2 = setCollectionSchema({
          selectedCollectionView: this.selectedCollectionView,
          collectionsSchemaMapping: setCollectionSchemaMapping(schema),
          collectionsSchema,
        });
      } catch (err) {
        console.log('getCollectionsSchema err', err);
      }
    },
    async getCollectionList() {
      const { getDefaultLibraryId: libraryId } = this;
      await Promise.all(this.collectionGroupsForFollowProduct.map(async item => (
        await this.getCollectionsList({
          libraryId,
          collectionGroup: item.group,
          limit: 99,
          showSpinner: false,
          isToAutocomplete: true,
          forProduct: true,
          accessToken: this.accessToken,
        })
      )));
    },
    async getLibraryCollectionList() {
      const { propLibraryId: libraryId, activeWorkspaceId: workspaceId } = this;
      const isAutocomplete = true;
      await this.getCustomLibraryCollectionsList({
        libraryId,
        workspaceId,
        isAutocomplete,
      });
    },
    async showProductModalToCreating() {
      this.selectedCollectionToCreateProduct = null;
      const { isSourceLibraryId } = this;
      isSourceLibraryId ? await this.getLibraryCollectionList() : await this.getCollectionList();
      if (!this.collectionsList.length) return;
      let collection = this.collectionsList[0];
      const route = this.$route;
      if (route.name == 'collection-library') {
        const colId = route.params.id;
        collection = this.collectionsList.find(col => col.id == colId);
      }
      if (!isLimitOfProducts(collection)) {
        this.selectedCollectionToCreateProduct = collection;
      }
      this.getSearchResultHeight();
    },
    async showProductModalInCollections() {
      this.spinner(true);
      const {
        collectionIdForViewSchema,
        creatorUnableToCreateFollowProducts: flag,
        useTheSameSchemaAndViews,
      } = this;
      const collectionId = this.sharedLinkCollectionId ? this.sharedLinkCollectionId : collectionIdForViewSchema;
      if (this.isAddModalVariant) {
        await this.showProductModalToCreating();
        if (flag) return;
      }
      if (flag && useTheSameSchemaAndViews) {
        this.spinner(false);
        this.getSearchResultHeight();
        return;
      }
      this.getSchemaAndViewsToCollection({
        collectionId,
      });
    },
    async onCloseModal() {
      this.rowData = {
      };
      this.fieldErrors = [];
      this.selectedCollectionToCreateProduct = null;
      this.collectionsSchemaV2 = {
      };
      this.selectedCollectionView = {
      };
      this.setProductToUpdate(null);
      this.changeProductModalVariant(null);
      this.changeProductCollectionLink('');
      this.changeProductCollectionName('');
    },
    checkValidateErrors() {
      let errors = [];
      this.formatedRequiredFields.forEach((f) => {
        if (!this.item[f] || !this.item[f].length) {
          errors.push(f);
        }
      });
      this.fieldErrors = errors;
    },
    async onSave() {
      if (this.showSpinner) {
        return;
      }
      const canSave = await this.allowToSave(this.currentProduct?.mode, this.isUpdateReferencedProduct ? this.item?.referencedProductId : this.currentProduct?.id);
      if (!canSave) {
        return;
      }
      this.checkValidateErrors();
      const {
        saveProduct,
        addProduct,
        isUpdateReferencedProduct,
        isEditModalRelatedVariant,
        closingModalLogic,
      } = this;
      if (this.fieldErrors.length === 0) {
        let productToCreate = [];
        this.schema.forEach((s) => {
          for (const element in this.item) {
            if (this.item[element] === undefined) {
              this.item[element] = '';
            }
          }
          if ((isEditModalRelatedVariant) && this.item[s.text] !== undefined
            || this.item && this.item[s.text]?.length !== 0 && this.item[s.text] !== undefined) {
            productToCreate.push(this.generateProduct({
              id: s.id,
              value: this.item[s.text] || '',
            }));
          }
        });
        const product = (isEditModalRelatedVariant) && !this.isCreateNewProduct ? await saveProduct({
          product: productToCreate,
          productId: this.item.id,
          ...(isUpdateReferencedProduct && {
            collectionId: this.item?.referencedCollectionId,
            productId: this.item?.referencedProductId,
          }),
        }) : await addProduct(productToCreate);
        closingModalLogic();
        this.$emit('savedProduct', product);
        this.isCreateNewProduct = false;
      }
    },
    closingModalLogic() {
      const { isDisallowClosingProductDialogAfterUpdate: isDisallow } = this;
      if (isDisallow) {
        this.getSearchResultHeight();
        this.changeProductModalVariant(TYPE_READONLY);
        return;
      }
      this.showProductModal = false;
    },
    showPensil(type) {
      return ['DATE', 'ARRAY_AIRTABLE_IMAGE', 'COMBOBOX', 'DROPDOWN'].includes(type);
    },
    getProductId(val) {
      const { SK } = val ?? {
      };
      if (!SK) {
        this.productId = '';
        return;
      }
      const [, productId] = SK.split('ROW#') || [];
      if (!productId) {
        this.productId = '';
        return;
      }
      this.productId = productId;
    },
    getInitialValue(item) {
      if (item.includes('ARRAY')) {
        return [];
      } else {
        return '';
      }
    },
    changeToEdit() {
      this.changeProductModalVariant(TYPE_EDIT);
      this.getSearchResultHeight();
    },
    async deleteCurrentProduct() {
      const {
        isEditableFromSearch,
        followedCollectionId: collectionId = '',
        productId = '',
        deleteProduct,
        deleteProductFromSearch,
        removeText: text,
        useActionsProductFromSearch: flag,
      } = this;
      const action = isEditableFromSearch && flag ? deleteProductFromSearch : deleteProduct;
      const res = await this.$openConfirm({
        text,
      });
      if (!res) return;
      try {
        this.spinner(true);
        await action({
          collectionId,
          productIds: [productId],
        });
      } finally {
        this.spinner(false);
        this.showProductModal = false;
      }
    },
  },
};
</script>
<style scoped lang="scss">
@import '~vuetify/src/styles/settings/_variables';
.add-product-modal {
  margin-left: auto;
}
.items-wrapper {
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  min-height: 660px;
}
::v-deep .item-wrapper {
  margin-top: 20px;
  height: auto;
}
.items {
  display: flex;
  flex-direction: column;
}
::v-deep .v-application p {
  margin-bottom: 0px !important;
}
.required-field {
  color: red;
}
::v-deep.v-btn {
  width: 154px;
}
.product-details-flex-wrapper {
  display: flex;
  column-gap: 20px;
  width: 100%;
  div:first-child {
    flex-grow: 3;
    flex-basis: 0;
  }
}
.product-details-aside-wrapper {
  padding: 0 4px;
  overflow: auto;
  height: 100%;
  flex-grow: 2;
  flex-basis: 0;
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    // The aside items show at the bottom of the original now.
    display: none;
  }
}
</style>
